import type { ReactNode } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  SettingsCardRow
} from 'design-system';

import type { Option } from '@/entities/user-config';

export type SettingsOption = Option & { info?: ReactNode };
export type SettingsOptionGroup = {
  label: string;
  options: SettingsOption[];
};
export type SettingsOptions = (SettingsOption | SettingsOptionGroup)[];

export type SettingsSelectRowProps = {
  id: string;
  label: string;
  value: Option;
  options: SettingsOptions;
  disabled?: boolean;
  onChange: (value: Option) => void;
};

const isOptionGroup = (
  option: SettingsOption | SettingsOptionGroup
): option is SettingsOptionGroup => 'options' in option;

const getOptionText = ({ label, info }: SettingsOption) =>
  typeof info === 'string' ? `${label} ${info}` : label;

const SettingsSelectRow = ({
  id,
  label,
  value,
  options,
  disabled = false,
  onChange
}: SettingsSelectRowProps) => {
  const flatOptions = options.flatMap(option =>
    isOptionGroup(option) ? option.options : [option]
  );
  const items = Object.fromEntries(
    flatOptions.map(option => [option.value, getOptionText(option)])
  );

  return (
    <SettingsCardRow
      label={label}
      labelFor={id}
    >
      <Select
        disabled={disabled}
        items={items}
        value={value.value}
        onValueChange={selectedValue => {
          const selected = flatOptions.find(option => option.value === selectedValue);
          if (selected) {
            onChange({ label: selected.label, value: selected.value });
          }
        }}
      >
        <SelectTrigger
          aria-label={label}
          className='w-full'
          id={id}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(option =>
            isOptionGroup(option) ? (
              <SelectGroup key={option.label}>
                <SelectLabel>{option.label}</SelectLabel>
                {option.options.map(groupOption => (
                  <SelectItem
                    key={groupOption.value}
                    value={groupOption.value}
                  >
                    {getOptionText(groupOption)}
                  </SelectItem>
                ))}
              </SelectGroup>
            ) : (
              <SelectItem
                key={option.value}
                value={option.value}
              >
                {getOptionText(option)}
              </SelectItem>
            )
          )}
        </SelectContent>
      </Select>
    </SettingsCardRow>
  );
};

export default SettingsSelectRow;
